import { useEffect, useMemo, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { loadStripe } from "@stripe/stripe-js";
import {
    CheckoutElementsProvider,
    PaymentElement,
    useCheckoutElements,
} from "@stripe/react-stripe-js/checkout";
import { Loader2, X } from "lucide-react";
import { Dialog, DialogPortal } from "@/components/ui/dialog";
import { FAIRSHARE_CHECKOUT_APPEARANCE } from "@/lib/stripeCheckoutAppearance";

const PLAN_LABELS = {
    pro: "Pro",
    business: "Business",
};

const CHECKOUT_LOAD_TIMEOUT_MS = 25000;

function formatCheckoutError(error) {
    if (!error) {
        return "Could not load checkout";
    }
    if (typeof error === "string") {
        return error;
    }
    if (typeof error.message === "string" && error.message.trim()) {
        return error.message;
    }
    return "Could not load checkout";
}

function BillingCheckoutPayForm({ planLabel, onComplete, onRedirectFallback, loadTimedOut }) {
    const checkoutState = useCheckoutElements();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (loadTimedOut && checkoutState.type === "loading") {
            setError(
                "Checkout is taking too long to load. This is often caused by mismatched Stripe test/live keys or a blocked Stripe script.",
            );
        }
    }, [loadTimedOut, checkoutState.type]);

    if (checkoutState.type === "loading" && !loadTimedOut) {
        return (
            <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3 text-white/50">
                <Loader2 className="h-5 w-5 animate-spin text-[#00BF0D]" aria-hidden />
                <p className="text-sm">Loading checkout…</p>
            </div>
        );
    }

    if (checkoutState.type === "error" || (checkoutState.type === "loading" && loadTimedOut)) {
        const message =
            checkoutState.type === "error"
                ? formatCheckoutError(checkoutState.error)
                : error || "Could not load checkout";
        return (
            <CheckoutErrorPanel
                message={message}
                onRedirectFallback={onRedirectFallback}
            />
        );
    }

    const { checkout } = checkoutState;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (busy) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const result = await checkout.confirm();
            if (result.type === "error") {
                setError(result.error.message || "Payment could not be completed");
                return;
            }
            onComplete?.();
        } catch (e) {
            setError(e?.message || "Payment could not be completed");
        } finally {
            setBusy(false);
        }
    };

    if (error && checkoutState.type === "success") {
        return (
            <div className="px-4 py-4">
                <p className="text-sm text-rose-300" role="alert">
                    {error}
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="billing-checkout-form">
            <div className="billing-checkout-form__fields px-2 pt-1">
                <PaymentElement options={{ layout: "tabs" }} />
            </div>
            <div className="border-t border-white/[0.08] px-4 py-3">
                <button
                    type="submit"
                    disabled={busy}
                    className="btn-neon w-full rounded-md px-4 py-2.5 text-sm font-medium disabled:opacity-60"
                    data-testid="billing-checkout-submit"
                >
                    {busy ? "Processing…" : `Subscribe to ${planLabel}`}
                </button>
            </div>
        </form>
    );
}

function CheckoutErrorPanel({ message, onRedirectFallback }) {
    return (
        <div className="space-y-3 px-4 py-6">
            <p className="text-sm text-rose-300" role="alert">
                {message}
            </p>
            {onRedirectFallback && (
                <button
                    type="button"
                    onClick={onRedirectFallback}
                    className="btn-ghost w-full rounded-md border border-white/10 px-4 py-2.5 text-sm text-white/80 hover:bg-white/[0.05]"
                    data-testid="billing-checkout-redirect-fallback"
                >
                    Continue on Stripe&apos;s secure checkout page
                </button>
            )}
        </div>
    );
}

export function BillingCheckoutModal({
    open,
    onOpenChange,
    publishableKey,
    clientSecret,
    plan,
    onComplete,
    onRedirectFallback,
    initError,
}) {
    const [stripeInstance, setStripeInstance] = useState(null);
    const [stripeLoadError, setStripeLoadError] = useState(null);
    const [loadTimedOut, setLoadTimedOut] = useState(false);

    const checkoutOptions = useMemo(
        () => ({
            clientSecret,
            elementsOptions: {
                appearance: FAIRSHARE_CHECKOUT_APPEARANCE,
            },
        }),
        [clientSecret],
    );

    useEffect(() => {
        if (!open || !publishableKey) {
            setStripeInstance(null);
            setStripeLoadError(null);
            setLoadTimedOut(false);
            return undefined;
        }

        let active = true;
        setStripeInstance(null);
        setStripeLoadError(null);
        setLoadTimedOut(false);

        loadStripe(publishableKey)
            .then((stripe) => {
                if (!active) {
                    return;
                }
                if (!stripe) {
                    setStripeLoadError(
                        "Stripe.js could not load. Check your publishable key or disable ad blockers.",
                    );
                    return;
                }
                if (typeof stripe.initCheckoutElementsSdk !== "function") {
                    setStripeLoadError(
                        "Stripe.js is outdated or blocked. Refresh the page or use the redirect checkout option.",
                    );
                    return;
                }
                setStripeInstance(stripe);
            })
            .catch((err) => {
                if (active) {
                    setStripeLoadError(err?.message || "Stripe.js could not load");
                }
            });

        return () => {
            active = false;
        };
    }, [open, publishableKey]);

    useEffect(() => {
        if (!open || !stripeInstance || !clientSecret) {
            setLoadTimedOut(false);
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setLoadTimedOut(true);
        }, CHECKOUT_LOAD_TIMEOUT_MS);

        return () => window.clearTimeout(timer);
    }, [open, stripeInstance, clientSecret]);

    const planLabel = PLAN_LABELS[plan] || plan || "paid";
    const canMountCheckout = Boolean(stripeInstance && clientSecret && !stripeLoadError && !initError);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogPortal>
                <DialogPrimitive.Overlay
                    className="billing-checkout-overlay fixed inset-0 z-50 overflow-y-auto overscroll-y-contain bg-black/85 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                >
                    <div className="flex min-h-full items-start justify-center p-4 py-8 sm:items-center sm:p-6">
                        <DialogPrimitive.Content
                            className="billing-checkout-dialog relative z-50 w-full max-w-md border border-white/10 bg-[#1E1E1E] text-white shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-xl"
                            data-testid="billing-checkout-modal"
                        >
                            <header className="shrink-0 space-y-1 border-b border-white/[0.08] px-4 py-3.5 sm:px-5">
                                <DialogPrimitive.Title className="font-heading text-base font-semibold text-white">
                                    Upgrade to {planLabel}
                                </DialogPrimitive.Title>
                                <DialogPrimitive.Description className="text-xs leading-relaxed text-white/50">
                                    Secure checkout powered by Stripe. Your plan updates when payment completes.
                                </DialogPrimitive.Description>
                            </header>

                            <div className="billing-embedded-checkout-scroll bg-[#1E1E1E]">
                                {initError ? (
                                    <CheckoutErrorPanel
                                        message={initError}
                                        onRedirectFallback={onRedirectFallback}
                                    />
                                ) : stripeLoadError ? (
                                    <CheckoutErrorPanel
                                        message={stripeLoadError}
                                        onRedirectFallback={onRedirectFallback}
                                    />
                                ) : !publishableKey || !clientSecret ? (
                                    <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3 text-white/50">
                                        <Loader2 className="h-5 w-5 animate-spin text-[#00BF0D]" aria-hidden />
                                        <p className="text-sm">Preparing checkout…</p>
                                    </div>
                                ) : !canMountCheckout ? (
                                    <div className="flex min-h-[12rem] flex-col items-center justify-center gap-3 text-white/50">
                                        <Loader2 className="h-5 w-5 animate-spin text-[#00BF0D]" aria-hidden />
                                        <p className="text-sm">Loading Stripe…</p>
                                    </div>
                                ) : (
                                    <CheckoutElementsProvider
                                        key={clientSecret}
                                        stripe={stripeInstance}
                                        options={checkoutOptions}
                                    >
                                        <BillingCheckoutPayForm
                                            planLabel={planLabel}
                                            onComplete={onComplete}
                                            onRedirectFallback={onRedirectFallback}
                                            loadTimedOut={loadTimedOut}
                                        />
                                    </CheckoutElementsProvider>
                                )}
                            </div>

                            <DialogPrimitive.Close
                                className="absolute right-3 top-3 rounded-md p-1.5 text-white/40 transition-colors hover:bg-white/5 hover:text-white/70 focus:outline-none focus:ring-2 focus:ring-[#00BF0D]/35"
                                aria-label="Close checkout"
                            >
                                <X className="h-4 w-4" />
                            </DialogPrimitive.Close>
                        </DialogPrimitive.Content>
                    </div>
                </DialogPrimitive.Overlay>
            </DialogPortal>
        </Dialog>
    );
}
